<script setup lang="ts">
import { inject, ref } from 'vue';
import { Download, Eye, Heart, ShoppingCart } from 'lucide-vue-next';
import { Slot } from 'reka-ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import useDownloads from '@/composables/useDownloads.ts';
import useFavorites from '@/composables/useFavorites.ts';
import useFile from '@/composables/useFile.ts';
import useFileSearch from '@/composables/useFileSearch.ts';
import { viewModeKey } from '@/stores/documents-layout-store.ts';

const props = defineProps<{
    uid: string;
    url: string;
    shopUrl: string;
    isNew?: boolean;
}>();

const viewMode = inject(viewModeKey, ref('grid'));
const favorites = useFavorites();
const { view } = useFile(props.uid);
const { trackDownload } = useDownloads();
const { isVisible } = useFileSearch(props.uid);
</script>

<template>
    <div
        v-if="isVisible"
        :key="uid"
        :class="{
            'flex flex-col gap-y-3 rounded-md border p-4': viewMode === 'grid',
            'col-span-full grid grid-cols-subgrid items-center py-3':
                viewMode === 'list',
        }"
    >
        <div
            v-if="viewMode === 'grid'"
            class="bg-muted relative -mx-3 -mt-3 flex h-62 items-center justify-center p-6"
        >
            <Slot class="shadow-sm">
                <slot name="preview" />
            </Slot>
            <Badge
                v-if="isNew"
                variant="secondary"
                class="pointer-events-none absolute top-3 left-3 z-10"
                >Neu</Badge
            >
            <button
                class="absolute inset-0 cursor-pointer"
                @click="view"
            >
                <span class="sr-only">Datei ansehen</span>
            </button>
        </div>
        <p
            class="p-small font-semibold"
            :class="{ 'line-clamp-2 min-w-0 break-words': viewMode === 'list' }"
        >
            <Slot @click.prevent="view">
                <slot name="title" />
            </Slot>
            <span class="text-muted-foreground mt-0.5 block text-sm font-light">
                <span
                    :class="{
                        'line-clamp-1': viewMode === 'list',
                        'line-clamp-2': viewMode !== 'list',
                    }"
                >
                    <slot name="subtitle" />
                </span>
            </span>
        </p>
        <div class="flex flex-wrap gap-0.5 overflow-hidden">
            <Badge
                v-if="isNew && viewMode === 'list'"
                variant="secondary"
                >Neu</Badge
            >
            <slot name="meta" />
        </div>
        <div class="mt-auto flex justify-end gap-0.5">
            <Button
                v-if="shopUrl"
                variant="ghost"
                size="icon-sm"
                as-child
            >
                <a
                    :href="shopUrl"
                    target="_blank"
                >
                    <ShoppingCart />
                </a>
            </Button>
            <Button
                variant="ghost"
                size="icon-sm"
                @click="view"
            >
                <Eye />
            </Button>
            <Button
                variant="ghost"
                size="icon-sm"
                @click="() => favorites.toggleDocument(uid)"
            >
                <Heart
                    :fill="favorites.isFavorite(uid) ? 'currentColor' : 'none'"
                />
            </Button>
            <Button
                variant="ghost"
                size="icon-sm"
                as-child
            >
                <a
                    :href="url"
                    download
                    @click="trackDownload(uid)"
                >
                    <Download />
                </a>
            </Button>
        </div>
    </div>
</template>
